import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { api } from '../../services/api';
import { repositorioFazenda } from '../../sync/repositories/fazendas';
import { repositorioAnimal } from '../../sync/repositories/pecuaria';
import { extrairMensagensErro } from '../../utils/errorUtils';

const ESTADO_INICIAL = {
  sisbov: '', numero_identificacao: '', fazenda: '', especie: '', raca: '', sexo: '', data_nascimento: '',
};

/**
 * Modal de cadastro rápido de Animal. Espécie/Raça vêm de catálogo fixo
 * do sistema (`/pecuaria/animal/especies/` e `/racas/?especie=`), não de
 * um cadastro editável pela empresa — por isso não têm botão "+" próprio.
 */
export default function ModalCadastroAnimal({ isOpen, onClose, onSuccess, fazendaSelecionadaId = null }) {
  const [dados, setDados] = useState(ESTADO_INICIAL);
  const [fazendas, setFazendas] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [racas, setRacas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setDados(ESTADO_INICIAL);
      setRacas([]);
      setErros([]);
      setSalvando(false);
      return;
    }
    repositorioFazenda.listar()
      .then((resp) => setFazendas(Array.isArray(resp.data) ? resp.data : (resp.data.results || [])))
      .catch(() => setFazendas([]));
    api.get('/pecuaria/animal/especies/')
      .then((resp) => setEspecies(Array.isArray(resp.data) ? resp.data : (resp.data.results || [])))
      .catch(() => setEspecies([]));
    setDados((prev) => ({ ...prev, fazenda: fazendaSelecionadaId || '' }));
  }, [isOpen, fazendaSelecionadaId]);

  useEffect(() => {
    if (!dados.especie) { setRacas([]); return; }
    api.get(`/pecuaria/animal/racas/?especie=${dados.especie}`)
      .then((resp) => setRacas(Array.isArray(resp.data) ? resp.data : (resp.data.results || [])))
      .catch(() => setRacas([]));
  }, [dados.especie]);

  const handleChange = (campo) => (e) => {
    const valor = e.target.value;
    setDados((prev) => ({ ...prev, [campo]: valor, ...(campo === 'especie' ? { raca: '' } : {}) }));
  };

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!dados.sisbov.trim()) { setErros(['O SISBOV é obrigatório.']); return; }
    if (!dados.numero_identificacao.trim()) { setErros(['O número do brinco é obrigatório.']); return; }
    if (!dados.fazenda) { setErros(['Selecione a fazenda.']); return; }
    if (!dados.especie) { setErros(['Selecione a espécie.']); return; }
    if (!dados.raca) { setErros(['Selecione a raça.']); return; }
    if (!dados.sexo) { setErros(['Selecione o sexo.']); return; }
    if (!dados.data_nascimento) { setErros(['Informe a data de nascimento.']); return; }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioAnimal.criar({
        sisbov: dados.sisbov.trim(),
        numero_identificacao: dados.numero_identificacao.trim(),
        fazenda: dados.fazenda,
        especie: dados.especie,
        raca: dados.raca,
        sexo: dados.sexo,
        data_nascimento: dados.data_nascimento,
        status: 'A',
        estado: 'S',
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Animal" size="lg">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SISBOV *</label>
            <input
              type="text"
              value={dados.sisbov}
              onChange={handleChange('sisbov')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              autoFocus
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Número do Brinco *</label>
            <input
              type="text"
              value={dados.numero_identificacao}
              onChange={handleChange('numero_identificacao')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
          <select
            value={dados.fazenda}
            onChange={handleChange('fazenda')}
            disabled={salvando}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
          >
            <option value="">Selecione...</option>
            {fazendas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Espécie *</label>
            <select
              value={dados.especie}
              onChange={handleChange('especie')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {especies.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Raça *</label>
            <select
              value={dados.raca}
              onChange={handleChange('raca')}
              disabled={salvando || !dados.especie}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">{dados.especie ? 'Selecione...' : 'Selecione a espécie primeiro'}</option>
              {racas.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sexo *</label>
            <select
              value={dados.sexo}
              onChange={handleChange('sexo')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              <option value="M">Macho</option>
              <option value="F">Fêmea</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento *</label>
            <input
              type="date"
              value={dados.data_nascimento}
              onChange={handleChange('data_nascimento')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
        </div>
        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
